import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, useTheme, useMediaQuery, Alert, CircularProgress, Avatar, Paper, Stack, Button } from '@mui/material';
import { SearchAndFilterBar, SkeletonLoader } from '../../components/Common';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import StudentForm from '../../components/Forms/StudentForm';
import DocumentGenerationModal from '../../components/Forms/DocumentGenerationModal';
import { studentService, classService } from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import { formatDate } from '../../utils/dateFormat';
import { showToast } from '../../utils/toast';
import { getAvatarColor, getInitials } from '../../utils/avatar';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';

const Students = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const drawerWidth = 280;

  // State for students data
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  
  // Document generation modal state
  const [docModalOpen, setDocModalOpen] = useState(false);

  // Search and filter state
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const filtersRef = useRef({});
  const isInitialLoadRef = useRef(true);
  const hasInitialFetchedRef = useRef(false);
  
  // Classes for filter options
  const [classes, setClasses] = useState([]);

  // Infinite scroll: server-side paging
  const pageSize = 20;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isAppending, setIsAppending] = useState(false);
  const sentinelRef = useRef(null);
  const fetchingRef = useRef(false); // guard to prevent concurrent/looped fetches
  const offsetRef = useRef(0); // mirror of offset to avoid stale closures
  const lastRequestKeyRef = useRef(null);

  // Fetch students from API
  const fetchStudents = useCallback(async (search = '', showSearchLoader = false, append = false, customOffset = null) => {
    // Prevent parallel or looped calls
    if (fetchingRef.current) {
      console.log('fetchStudents: Already fetching, skipping');
      return;
    }
    
    // Create a unique request key to prevent duplicate requests
    const currentFilters = filtersRef.current;
    const nextOffset = customOffset !== null ? customOffset : (append ? offsetRef.current : 0);
    const requestKey = `${search.trim()}_${currentFilters.class_name || ''}_${nextOffset}_${append}`;
    
    // If this is the exact same request as the last one, skip it
    if (lastRequestKeyRef.current === requestKey && !append) {
      console.log('fetchStudents: Duplicate request detected, skipping', requestKey);
      return;
    }
    
    lastRequestKeyRef.current = requestKey;
    fetchingRef.current = true;
    console.log('fetchStudents: Starting fetch', { search, append, customOffset, requestKey });

    // Temporarily unobserve while fetching to avoid repeated triggers
    const sentinelEl = sentinelRef.current;
    let observerToRestore = null;
    if (sentinelEl && sentinelEl._io) {
      observerToRestore = sentinelEl._io;
      try { observerToRestore.unobserve(sentinelEl); } catch (e) {}
    }

    try {
      if (showSearchLoader) {
        setSearchLoading(true);
      } else if (!append) {
        setLoading(true);
      } else {
        setIsAppending(true);
      }
      setError(null);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (search.trim()) {
        params.append('search', search.trim());
      }
      if (currentFilters.class_name) {
        params.append('class_id', currentFilters.class_name);
      }
      // server-side pagination with explicit offset
      params.append('limit', String(pageSize));
      params.append('offset', String(nextOffset));
      
      const url = `${API_ENDPOINTS.STUDENTS}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        const fetchedStudents = data.data || [];
        setStudents(prev => append ? [...prev, ...fetchedStudents] : fetchedStudents);
        setFilteredStudents(prev => append ? [...prev, ...fetchedStudents] : fetchedStudents);
        setHasMore(fetchedStudents.length === pageSize);
        setOffset(nextOffset + fetchedStudents.length);
        offsetRef.current = nextOffset + fetchedStudents.length;
      } else {
        const errorMsg = data.error || 'Failed to fetch students';
        setError(errorMsg);
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      const errorMsg = err.message || 'Failed to fetch students';
      setError(errorMsg);
      setHasMore(false);
    } finally {
      setTimeout(() => {
          setSearchLoading(false);
          setLoading(false);
        setIsAppending(false);
        fetchingRef.current = false;
        // Re-observe after fetch completes
        const el = sentinelRef.current;
        if (el && el._io) {
          try { el._io.observe(el); } catch (e) {}
        }
      }, 0);
    }
  }, []); // No dependencies - use refs for filters

  // Fetch classes for filter options
  const fetchClasses = useCallback(async () => {
    try {
      const response = await classService.getAll();
      if (response.success) {
        setClasses(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  }, []);

  // Initialize filtersRef
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initial load - only run once using empty dependency array
  useEffect(() => {
    if (hasInitialFetchedRef.current) return;
    hasInitialFetchedRef.current = true;
    isInitialLoadRef.current = false;
    setOffset(0);
    offsetRef.current = 0;
    setHasMore(true);
    fetchStudents('', false, false, 0);
    fetchClasses(); // Fetch classes for filter options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - only run once on mount

  // Reload students when filter changes
  useEffect(() => {
    if (isInitialLoadRef.current) return; // Skip on initial load
    lastRequestKeyRef.current = null; // Reset to allow new request
    setOffset(0);
    offsetRef.current = 0;
    setHasMore(true);
    fetchStudents('', false, false, 0);
  }, [filters.class_name]); // Only trigger on filter change

  // Search on typing with debounce
  useEffect(() => {
    if (isInitialLoadRef.current) return;

    const timer = setTimeout(() => {
      lastRequestKeyRef.current = null; // Reset to allow new request
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);
      fetchStudents(searchTerm, true, false, 0);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update filteredStudents when students change (backend handles filtering)
  useEffect(() => {
    // Backend already filters by class_id, so just use students directly
    setFilteredStudents(students);
  }, [students]);

  // Intersection observer to append more
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (!first || !first.isIntersecting) return;
      // Only append after the first page is loaded (offset > 0)
      if (hasMore && offsetRef.current > 0 && !isAppending && !loading && !searchLoading && !fetchingRef.current) {
        fetchStudents(searchTerm, false, true);
      }
    }, { root: null, rootMargin: '300px', threshold: 0 });

    // store ref to allow temporary unobserve during fetch
    el._io = io;
    io.observe(el);
    return () => {
      try { io.disconnect(); } catch (e) {}
      if (el && el._io === io) { el._io = null; }
    };
  }, [hasMore, isAppending, loading, searchLoading, searchTerm]); // Remove fetchStudents from deps since it's stable now

  // Search and filter handlers - memoized to prevent SearchAndFilterBar re-renders
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleFilter = useCallback((filterValues) => {
    setFilters(filterValues);
    filtersRef.current = filterValues; // Update ref immediately
  }, []);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    setFilters({});
    filtersRef.current = {}; // Update ref
  }, []);

  const handleAdd = useCallback(() => {
    // Navigate to the new Add Student page
    navigate('/users/students/add');
  }, [navigate]);

  const handleEdit = useCallback((student) => {
    navigate(`/users/students/edit/${student.id}`);
  }, [navigate]);

  const handleView = useCallback((student) => {
    navigate(`/users/students/${student.id}`);
  }, [navigate]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingStudent(null);
  }, []);

  const handleModalSave = useCallback(async (formData) => {
    try {
      setModalLoading(true);
      setError(null);
      
      const response = await fetch(API_ENDPOINTS.STUDENTS, {
        method: editingStudent ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showToast.success(editingStudent ? 'Student updated successfully' : 'Student created successfully');
        // Reset form state before closing modal
        setEditingStudent(null);
        setModalOpen(false);
        // Reset pagination and refetch from the beginning with current filters
        setOffset(0);
        offsetRef.current = 0;
        setHasMore(true);
        lastRequestKeyRef.current = null; // Reset to allow new request
        await fetchStudents(searchTerm, false, false, 0); // Refresh the list with current search and filters
      } else {
        const errorMsg = data.error || 'Failed to save student';
        showToast.error(errorMsg);
        // Don't set error state for save errors - keep table visible, error shown via toast
      }
    } catch (err) {
      console.error('Error saving student:', err);
      const errorMsg = err.message || 'Failed to save student';
      showToast.error(errorMsg);
      // Don't set error state for save errors - keep table visible, error shown via toast
    } finally {
      setModalLoading(false);
    }
  }, [editingStudent, fetchStudents, searchTerm]);

  // Filter options for SearchAndFilter component - memoized to prevent SearchAndFilterBar re-renders
  // Use classes from API with "Class " prefix
  const filterOptions = useMemo(() => {
    if (!classes || classes.length === 0) {
      return [];
    }
    
    return [{
      field: 'class_name',
      label: 'Class',
      options: classes.map(cls => {
        // Add "Class " prefix if not already present
        const className = cls.name?.startsWith('Class ') ? cls.name : `Class ${cls.name || ''}`;
        const label = cls.section ? `${className} - ${cls.section}` : className;
        return {
          value: cls.id,
          label: label
        };
      })
    }];
  }, [classes]);

  // derive a compact label for class
  const getClassLabel = useCallback((row) => {
    if (!row) return '—';
    const base = row.class_name?.toString() || '';
    const name = base.startsWith('Class ') ? base : (base ? `Class ${base}` : '');
    return row.class_section ? `${name} - ${row.class_section}` : name || '—';
  }, []);


  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar 
          open={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          drawerWidth={drawerWidth} 
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            {/* Custom Header with Title and Generate Documents Button */}
            <Box sx={{ mb: { xs: 1.5, md: 2 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography 
                variant="h6" 
                component="h1" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'text.primary',
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
                  letterSpacing: '0.01em'
                }}
              >
                Students Management
              </Typography>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => setDocModalOpen(true)}
                size="small"
                sx={{
                  whiteSpace: 'nowrap',
                }}
              >
                Generate Documents
              </Button>
            </Box>

            <SearchAndFilterBar
              title=""
              onSearch={handleSearch}
              onFilter={handleFilter}
              onCreate={handleAdd}
              onClear={handleClear}
              searchPlaceholder="Search by name..."
              filterOptions={filterOptions}
              createButtonText="Add Student"
              searchValue={searchTerm}
            />
            
            {/* Card grid - responsive, compact, left-to-right flow */}
            <Box sx={{ mt: 2 }}>
              {loading && !searchLoading ? (
                <SkeletonLoader variant="list" count={5} />
              ) : error && !searchLoading ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : (
                <>
                      {filteredStudents.length === 0 ? (
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 4, 
                        borderRadius: 3, 
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 'calc(100vh - 300px)',
                        width: '100%'
                      }}
                    >
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        No students found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search or filters to view the list
                      </Typography>
                    </Paper>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr', // single card per row
                        gap: { xs: 1.5, sm: 2 },
                      }}
                    >
                      {filteredStudents.map((row) => {
                        const isActive = row.is_active === '1' || row.is_active === 1 || row.is_active === true;
                        return (
                          <Paper
                            key={row.id}
                            variant="outlined"
                            sx={{
                              p: { xs: 2, sm: 2.4 },
                              borderRadius: 2,
                              borderColor: 'divider'
                            }}
                          >
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                <Avatar
                                  src={row.profile_image || null}
                                  alt={row.full_name}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: getAvatarColor(row.full_name),
                                    color: '#ffffff',
                                    fontWeight: 700,
                                  }}
                                >
                                  {!row.profile_image && getInitials(row.full_name)}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                      {row.full_name}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={isActive ? 'Active' : 'Inactive'}
                                      color={isActive ? 'success' : 'default'}
                                      variant="outlined"
                                    />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Roll:</strong> {row.roll_number ?? '—'} &nbsp;·&nbsp;
                                    <strong>Class:</strong> {getClassLabel(row)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>DOB:</strong> {formatDate(row.date_of_birth)} &nbsp;·&nbsp;
                                    <strong>Admission:</strong> {formatDate(row.admission_date)}
                                  </Typography>
                                </Box>
                              </Stack>
                              {/* Middle: parent details - hidden on mobile */}
                              <Stack spacing={0.5} sx={{ flex: { xs: '1 1 100%', md: '0 0 280px' }, minWidth: { xs: '100%', md: 240 }, display: { xs: 'none', md: 'flex' } }}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Parent:</strong> {row.parent_name || '—'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Contact:</strong> {row.parent_phone || '—'}
                                </Typography>
                                {row.parent_email ? (
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Email:</strong> {row.parent_email}
                                  </Typography>
                                ) : null}
                              </Stack>
                              {/* Right: actions */}
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flex: { xs: '1 1 100%', md: '0 0 220px' }, minWidth: { xs: '100%', md: 180 }, flexWrap: 'wrap', rowGap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handleView(row)}
                                  sx={{ flex: { xs: 1, md: 'unset' } }}
                                >
                                  View
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<EditIcon />}
                                  onClick={() => handleEdit(row)}
                                  sx={{ flex: { xs: 1, md: 'unset' } }}
                                >
                                  Edit
                                </Button>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                      {/* Sentinel for infinite scroll */}
                      <Box ref={sentinelRef} sx={{ height: 8 }} />
                      {(isAppending) && (
                        <Box display="flex" justifyContent="center" p={2}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                      {!hasMore && filteredStudents.length > 0 && (
                        <Box display="flex" justifyContent="center" p={2}>
                          <Typography variant="caption" color="text.secondary">End of list</Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* Student Form Modal - Only for editing */}
            {editingStudent && (
              <StudentForm
                open={modalOpen}
                onClose={handleModalClose}
                onSubmit={handleModalSave}
                initialData={editingStudent}
                title="Edit Student"
              />
            )}

            {/* Document Generation Modal */}
            <DocumentGenerationModal
              open={docModalOpen}
              onClose={() => setDocModalOpen(false)}
              classes={classes}
              onGenerate={() => {
                showToast('PDF generated successfully!', 'success');
              }}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Students;
